import type { DndContextProps, DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { MouseSensor, TouchSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove } from '@dnd-kit/sortable';
import { useMemo, useState } from 'react';
import type { GetManyEventsListItem } from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import { getEventMetadata } from '../../../utils/event-utils.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { getSortableEventId } from '../../events/utils/get-sortable-event-id.ts';

export type DayEventsBoardParams = {
    day: string;
    ownerId: string;
};

export type DayEventsBoard = {
    isLoading: boolean;
    events: GetManyEventsListItem[];
    sortedIds: string[];
    draggedEvent: GetManyEventsListItem | undefined;
    /** Spread onto the `DndContext` that wraps the day's list */
    dndContextProps: Pick<
        DndContextProps,
        'sensors' | 'collisionDetection' | 'modifiers' | 'onDragStart' | 'onDragEnd'
    >;
};

/**
 * A day's events, with drag and drop to reorder them. Copied from `EventsOverviewContentDay`
 * until the old page is removed.
 */
export function useDayEventsBoard({ day, ownerId }: DayEventsBoardParams): DayEventsBoard {
    const apiUtils = api.useUtils();
    const params = { dateFrom: day, dateTo: day, ownerId };
    const { data, isLoading } = api.platoUser.query.events.getList.useQuery(params);
    const [activeId, setActiveId] = useState<string | null>(null);
    const sendEvent = useAnalytics();

    const reorderEvents = api.platoUser.command.events.reorder.useMutation({
        onSettled: () => {
            void apiUtils.platoUser.query.events.getList.invalidate();
        },
    });

    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    );

    const sortedIds = useMemo(() => data?.map((event) => getSortableEventId(event)) ?? [], [data]);

    const handleDragStart = (event: DragStartEvent): void => {
        setActiveId(event.active.id.toString());
    };

    const handleDragEnd = (event: DragEndEvent): void => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = sortedIds.indexOf(active.id.toString());
            const newIndex = sortedIds.indexOf(over.id.toString());

            const events_order = arrayMove(sortedIds, oldIndex, newIndex);

            void apiUtils.platoUser.query.events.getList.cancel(params);

            const oldItems = apiUtils.platoUser.query.events.getList.getData(params) ?? [];
            const newItems: typeof oldItems = [];

            for (const event_id of events_order) {
                const oldItem = oldItems.find((event) => getSortableEventId(event) === event_id);
                if (oldItem) {
                    newItems.push(oldItem);
                }
            }

            apiUtils.platoUser.query.events.getList.setData(params, newItems);

            // Track event reordering
            const reorderedEvent = oldItems.find((event) => getSortableEventId(event) === active.id);
            if (reorderedEvent) {
                sendEvent('event_reordered', getEventMetadata(reorderedEvent));
            }

            reorderEvents.mutate({
                events_order: newItems.map((event) => event.event_id),
                date: day,
            });
        }

        setActiveId(null);
    };

    const events = data ?? [];

    return {
        isLoading,
        events,
        sortedIds,
        draggedEvent: events.find((event) => getSortableEventId(event) === activeId),
        dndContextProps: {
            sensors,
            collisionDetection: closestCenter,
            modifiers: [restrictToVerticalAxis],
            onDragStart: handleDragStart,
            onDragEnd: handleDragEnd,
        },
    };
}
